import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collectionGroup,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

function Transactions() {
  const [txns, setTxns] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const q = query(
        collectionGroup(db, "transactions"),
        orderBy("timestamp", "desc")
      );

      const snap = await getDocs(q);

      const data = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setTxns(data);
    } catch (e) {
      console.error("LOAD ERROR:", e);
    }
  };

  const filtered = txns.filter((t) => {
    if (filter === "all") return true;
    return t.type === filter;
  });

  return (
    <div style={container}>
      <h1 style={title}>💰 Transactions</h1>

      <TransactionsChart txns={txns} />

      <FilterTabs filter={filter} setFilter={setFilter} txns={txns} />

      {filtered.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No transactions</p>
      ) : (
        <div style={grid}>
          {filtered.map((t) => {
            const isCredit = t.type === "credit";

            return (
              <div key={t.id} style={card}>
                <div style={row}>
                  <span
                    style={{
                      color: isCredit ? "#22c55e" : "#ef4444",
                      fontWeight: "bold",
                    }}
                  >
                    ● {t.type?.toUpperCase()}
                  </span>

                  <span
                    style={{
                      color: isCredit ? "#22c55e" : "#ef4444",
                      fontWeight: "bold",
                    }}
                  >
                    {isCredit ? "+" : "-"}₹{t.amount}
                  </span>
                </div>

                <p style={text}>Status: {t.status}</p>
                <p style={text}>User: {t.userId || "N/A"}</p>
                <p style={text}>
                  Tournament: {t.tournamentId || "N/A"}
                </p>

                <p style={time}>
                  {t.timestamp?.seconds
                    ? new Date(
                        t.timestamp.seconds * 1000
                      ).toLocaleString()
                    : "N/A"}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Transactions;

/* 🔥 GRAPH */

function TransactionsChart({ txns }) {
  const map = {};

  txns.forEach((t) => {
    if (!t.timestamp?.seconds) return;

    const date = new Date(
      t.timestamp.seconds * 1000
    ).toLocaleDateString();

    if (!map[date]) {
      map[date] = { date, credit: 0, debit: 0 };
    }

    if (t.type === "credit") {
      map[date].credit += t.amount || 0;
    } else {
      map[date].debit += t.amount || 0;
    }
  });

  const data = Object.values(map);

  return (
    <div style={chartBox}>
      <h3 style={{ marginBottom: 10 }}>📊 Transactions Graph</h3>

      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={data}>
          <CartesianGrid stroke="#2A2F4A" />
          <XAxis dataKey="date" stroke="#aaa" />
          <YAxis stroke="#aaa" />
          <Tooltip />

          <Line
            type="monotone"
            dataKey="credit"
            stroke="#22c55e"
            strokeWidth={2}
          />

          <Line
            type="monotone"
            dataKey="debit"
            stroke="#ef4444"
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* 🔥 FILTER */

const FilterTabs = ({ filter, setFilter, txns }) => {
  const total = txns.length;
  const credit = txns.filter((t) => t.type === "credit").length;
  const debit = txns.filter((t) => t.type === "debit").length;

  const tabs = [
    { key: "all", label: "All", count: total },
    { key: "credit", label: "Credit", count: credit },
    { key: "debit", label: "Debit", count: debit },
  ];

  return (
    <div style={tabContainer}>
      {tabs.map((t) => (
        <div
          key={t.key}
          onClick={() => setFilter(t.key)}
          style={{
            ...tab,
            background:
              filter === t.key
                ? t.key === "credit"
                  ? "#22c55e"
                  : t.key === "debit"
                  ? "#ef4444"
                  : "#3b82f6"
                : "#1A1F2E",
            color: filter === t.key ? "#000" : "#fff",
          }}
        >
          {t.label} ({t.count})
        </div>
      ))}
    </div>
  );
};

/* 🔥 STYLES */

const container = {
  padding: 20,
  color: "white",
  background: "linear-gradient(135deg, #0F1221, #0A0D1A)",
  minHeight: "100vh",
};

const title = {
  marginBottom: 20,
};

const chartBox = {
  background: "#1A1F2E",
  padding: 16,
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.05)",
  marginBottom: 20,
};

const tabContainer = {
  display: "flex",
  gap: 10,
  marginBottom: 20,
  flexWrap: "wrap",
};

const tab = {
  padding: "8px 16px",
  borderRadius: 20,
  cursor: "pointer",
  fontWeight: "bold",
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: 16,
};

const card = {
  background: "#1A1F2E",
  padding: 14,
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.05)",
};

const row = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: 8,
};

const text = {
  fontSize: 13,
  opacity: 0.8,
};

const time = {
  fontSize: 12,
  opacity: 0.5,
};